import { useCallback, useEffect, useRef, useState } from "react";
import type { ChannelType } from "@workspace/api-client-react";
import type {
  ChannelNowPlaying,
  QueueSnapshotItem,
} from "@workspace/player-core";
import { nextBackoffMs } from "@/player/lib/reconnect-backoff";
import { shouldForceReconnect } from "@/lib/realtime-liveness";
import { getApiWebSocketOrigin } from "@/lib/api-config";
import { readClientReport } from "@/player/lib/client-report";
import {
  readClientAdvisory,
  type ClientAdvisory,
} from "@/player/lib/client-advisory";

export type PlaybackState = "playing" | "paused" | "stopped";
export type DeviceStatus = "online" | "idle" | "offline" | "error";

/** A now-playing snapshot the player publishes to the control surfaces. */
export interface NowPlayingPayload {
  state: PlaybackState;
  channelType?: ChannelType;
  channelId?: string | null;
  playlistId?: string | null;
  title?: string | null;
  artist?: string | null;
  itemId?: string | null;
  /**
   * The entry is loaded on the bus but has never sounded — a silent load under
   * a standing pause (see player-core `isCuedReport`). The frame still names
   * the item so the console shows what the venue is cued on; the gateway reads
   * this to keep the item out of the play-log gate until it actually plays.
   */
  cued?: boolean;
  positionSec?: number;
  volume?: number;
  durationSec?: number | null;
  queue?: QueueSnapshotItem[];
  /** Per-channel transport snapshot (audio bus + screen bus). */
  channels?: ChannelNowPlaying[];
  /**
   * Multi-room drift of the music bus vs the server segment timeline
   * (actual − expected seconds; positive = ahead). Present only while the
   * drift loop is measuring against an active sync timeline.
   */
  driftSec?: number;
}

/** A command envelope dispatched from a control peer (or the local UI). */
export interface RealtimeCommand {
  commandId: string;
  command: string;
  args: Record<string, unknown>;
  from?: string;
  ts?: number;
}

/** What a command handler may return; defaults to a successful ack. */
export interface CommandOutcome {
  ok: boolean;
  result?: Record<string, unknown>;
}

export type CommandHandler = (
  cmd: RealtimeCommand,
) => CommandOutcome | void | Promise<CommandOutcome | void>;

/** A WebRTC signaling message relayed from a control (broadcaster) peer. */
export interface SignalMessage {
  from: string;
  fromKind: string;
  payload: unknown;
}

export type SignalHandler = (sig: SignalMessage) => void;

export interface PlayerRealtimeOptions {
  token: string | null;
  onCommand?: CommandHandler;
  onSignal?: SignalHandler;
  /** Called when the server ends a live broadcast (e.g. presenter disconnect). */
  onBroadcastEnded?: (reason: string) => void;
}

export interface PlayerRealtime {
  /** WebSocket is open and the device is authenticated. */
  connected: boolean;
  /** The browser reports a network connection. */
  online: boolean;
  deviceId: string | null;
  deviceName: string | null;
  /**
   * The server's verdict on the build this player is running, or null when it
   * has nothing to say (the default). Advisory only: it is rendered, never
   * acted on by the playback engine.
   */
  advisory: ClientAdvisory | null;
  sendNowPlaying: (payload: NowPlayingPayload) => void;
  sendStatus: (status: DeviceStatus) => void;
  /** Relay a WebRTC signaling payload back toward the control peers. */
  sendSignal: (payload: unknown) => void;
  /** Publish a device health metric (playback.failed, command latency, etc.). */
  sendDeviceMetric: (metric: string, payload?: Record<string, unknown>) => void;
  /** Report offline-cache readiness (desktop shell only; web is online-only). */
  sendCacheReport: (report: unknown) => void;
}

const HEARTBEAT_INTERVAL_MS = 25_000;
const MIN_BACKOFF_MS = 1_000;
const MAX_BACKOFF_MS = 30_000;
/** Abort a handshake stuck in CONNECTING (network black hole) after this. */
const CONNECT_TIMEOUT_MS = 15_000;

function asRecord(v: unknown): Record<string, unknown> {
  return typeof v === "object" && v !== null
    ? (v as Record<string, unknown>)
    : {};
}

/**
 * The player's single realtime connection to the gateway. It authenticates with
 * the screen credential (via the `device.<token>` subprotocol so the secret stays out
 * of logs), keeps the device alive with periodic heartbeats, applies inbound
 * commands through `onCommand` and reports their result, and exposes imperative
 * senders for now-playing/status. Reconnects with jittered exponential backoff
 * and tracks browser online/offline so the engine can fall back to cached media.
 */
export function usePlayerRealtime(
  options: PlayerRealtimeOptions,
): PlayerRealtime {
  const { token } = options;

  const [connected, setConnected] = useState(false);
  const [online, setOnline] = useState(() =>
    typeof navigator === "undefined" ? true : navigator.onLine,
  );
  const [deviceId, setDeviceId] = useState<string | null>(null);
  const [deviceName, setDeviceName] = useState<string | null>(null);
  const [advisory, setAdvisory] = useState<ClientAdvisory | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  // Distinguishes a reconnect from the first connect after page load: only a
  // reconnect needs to trigger a schedule resync (the initial sync fetch is
  // already in flight on load). Survives effect re-runs on token change.
  const wasConnectedBeforeRef = useRef(false);
  const onCommandRef = useRef<CommandHandler | undefined>(options.onCommand);
  onCommandRef.current = options.onCommand;
  const onSignalRef = useRef<SignalHandler | undefined>(options.onSignal);
  onSignalRef.current = options.onSignal;
  const onBroadcastEndedRef = useRef(options.onBroadcastEnded);
  onBroadcastEndedRef.current = options.onBroadcastEnded;

  const send = useCallback((message: unknown) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(message));
    }
  }, []);

  const sendNowPlaying = useCallback(
    (payload: NowPlayingPayload) => {
      send({
        type: "now-playing",
        positionSec: 0,
        volume: 100,
        ...payload,
      });
    },
    [send],
  );

  const sendStatus = useCallback(
    (status: DeviceStatus) => {
      send({ type: "status", status });
    },
    [send],
  );

  const sendSignal = useCallback(
    (payload: unknown) => {
      send({ type: "signal", payload });
    },
    [send],
  );

  const sendDeviceMetric = useCallback(
    (metric: string, payload?: Record<string, unknown>) => {
      send({ type: "device-metric", metric, payload: payload ?? {} });
    },
    [send],
  );

  const sendCacheReport = useCallback(
    (report: unknown) => {
      send({ type: "cache-report", report });
    },
    [send],
  );

  useEffect(() => {
    if (!token) {
      setConnected(false);
      return;
    }

    // `disposed` guards every async/scheduled path from firing after cleanup
    // (token change, unmount) so we never resurrect a dead connection.
    let disposed = false;
    // Previous reconnect delay; 0 means "next attempt starts at MIN_BACKOFF_MS".
    let backoff = 0;
    let heartbeatTimer: number | undefined;
    let reconnectTimer: number | undefined;
    let connectTimeoutTimer: number | undefined;
    // Wall-clock of the last inbound frame on the CURRENT socket. The gateway
    // acks every heartbeat, so a healthy socket produces inbound traffic at
    // least once per interval — prolonged silence means a half-open TCP
    // connection (venue wifi drop, NAT timeout) that the browser will keep
    // reporting OPEN for minutes while every command sent to us vanishes and
    // the header still shows "Live".
    let lastInboundAt = Date.now();

    const clearTimers = () => {
      if (heartbeatTimer) {
        clearInterval(heartbeatTimer);
        heartbeatTimer = undefined;
      }
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = undefined;
      }
      if (connectTimeoutTimer) {
        clearTimeout(connectTimeoutTimer);
        connectTimeoutTimer = undefined;
      }
    };

    // Drop the previous socket without side effects: handlers detached first,
    // so its onclose can never schedule a reconnect for a connection we
    // deliberately replaced. Without this, an `online` event during
    // CONNECTING left the old socket armed — its eventual close spawned a
    // SECOND reconnect loop, the gateway indexed both sockets for this
    // device, and every command fanned out twice (double skips).
    const teardownSocket = () => {
      const old = wsRef.current;
      if (!old) return;
      wsRef.current = null;
      old.onopen = null;
      old.onmessage = null;
      old.onclose = null;
      old.onerror = null;
      try {
        old.close();
      } catch {
        /* already closed */
      }
    };

    const scheduleReconnect = () => {
      if (disposed) return;
      backoff = nextBackoffMs(backoff, {
        min: MIN_BACKOFF_MS,
        max: MAX_BACKOFF_MS,
      });
      reconnectTimer = window.setTimeout(connect, backoff);
    };

    const handleCommand = async (cmd: RealtimeCommand) => {
      const started = performance.now();
      let outcome: CommandOutcome = { ok: true };
      try {
        const handler = onCommandRef.current;
        const res = handler ? await handler(cmd) : undefined;
        if (res) outcome = res;
      } catch (err) {
        outcome = {
          ok: false,
          result: { error: err instanceof Error ? err.message : String(err) },
        };
      }
      const latencyMs = Math.round(performance.now() - started);
      send({
        type: "command-result",
        commandId: cmd.commandId,
        ok: outcome.ok,
        result: outcome.result ?? {},
        latencyMs,
      });
    };

    // Build identity of the shell running this bundle. Static for the life of
    // the page (a browser reload is what changes it), so it is read once here
    // and repeated verbatim on every heartbeat.
    const clientReport = readClientReport();

    const connect = () => {
      if (disposed) return;
      const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      const wsBase =
        getApiWebSocketOrigin() ?? `${protocol}//${window.location.host}`;
      const wsUrl = `${wsBase}/api/realtime`;
      // Only one live socket per hook instance, ever.
      teardownSocket();
      if (heartbeatTimer) {
        clearInterval(heartbeatTimer);
        heartbeatTimer = undefined;
      }
      // Token rides the subprotocol (device.<token>), not the query string, so
      // it never lands in proxy/access logs or browser history.
      const ws = new WebSocket(wsUrl, [`device.${token}`]);
      wsRef.current = ws;

      // A handshake against a network black hole sits in CONNECTING until the
      // OS gives up (minutes). Abort it ourselves so the backoff loop keeps
      // driving instead of silently stalling.
      connectTimeoutTimer = window.setTimeout(() => {
        connectTimeoutTimer = undefined;
        if (wsRef.current === ws && ws.readyState === WebSocket.CONNECTING) {
          ws.close();
        }
      }, CONNECT_TIMEOUT_MS);

      ws.onopen = () => {
        if (disposed || wsRef.current !== ws) {
          ws.close();
          return;
        }
        backoff = 0;
        lastInboundAt = Date.now();
        if (connectTimeoutTimer) {
          clearTimeout(connectTimeoutTimer);
          connectTimeoutTimer = undefined;
        }
        // Announce ourselves immediately, then on a steady cadence. The build
        // fields ride the heartbeat frame flat, which is where the gateway
        // reads them from (api-server parseClientReport).
        send({ type: "heartbeat", status: "online", ...clientReport });
        heartbeatTimer = window.setInterval(() => {
          if (wsRef.current !== ws || ws.readyState !== WebSocket.OPEN) return;
          if (
            shouldForceReconnect(lastInboundAt, Date.now(), HEARTBEAT_INTERVAL_MS)
          ) {
            // Two silent heartbeat intervals (the gateway acks every one):
            // the connection is dead even though readyState says OPEN. Close
            // it so onclose reconnects — otherwise the kiosk shows "Live"
            // while every operator command silently vanishes.
            ws.close();
            return;
          }
          ws.send(
            JSON.stringify({
              type: "heartbeat",
              status: "online",
              ...clientReport,
            }),
          );
        }, HEARTBEAT_INTERVAL_MS);
      };

      ws.onmessage = (event) => {
        lastInboundAt = Date.now();
        let data: Record<string, unknown>;
        try {
          data = asRecord(JSON.parse(event.data as string));
        } catch {
          return;
        }
        switch (data["type"]) {
          case "welcome":
            setConnected(true);
            setDeviceId((data["deviceId"] as string) ?? null);
            setDeviceName((data["deviceName"] as string) ?? null);
            // A reconnect may have missed schedule-updated pushes while the
            // socket was down; nudge the sync hook to refetch immediately
            // instead of waiting for the next poll.
            if (wasConnectedBeforeRef.current) {
              window.dispatchEvent(
                new CustomEvent("omnimedia:realtime-connected"),
              );
            }
            wasConnectedBeforeRef.current = true;
            break;
          case "command":
            void handleCommand({
              commandId: String(data["commandId"] ?? ""),
              command: String(data["command"] ?? ""),
              args: asRecord(data["args"]),
              from: data["from"] as string | undefined,
              ts: data["ts"] as number | undefined,
            });
            break;
          case "signal":
            onSignalRef.current?.({
              from: String(data["from"] ?? ""),
              fromKind: String(data["fromKind"] ?? ""),
              payload: data["payload"],
            });
            break;
          case "broadcast-ended":
            onBroadcastEndedRef.current?.(
              String(data["reason"] ?? "ended"),
            );
            break;
          case "announcement-started":
          case "announcement-ended":
          case "remote-desktop-started":
          case "remote-desktop-ended":
            window.dispatchEvent(
              new CustomEvent("omnimedia:live-session", { detail: data }),
            );
            break;
          case "schedule-updated":
            window.dispatchEvent(
              new CustomEvent("omnimedia:schedule-updated"),
            );
            break;
          case "device-updated":
            window.dispatchEvent(
              new CustomEvent("omnimedia:device-updated"),
            );
            break;
          case "client-advisory":
            // Advice about the build we are running. Rendering it is the only
            // response: playback is never interrupted over a version verdict.
            // A frame with a null advisory clears the notice.
            setAdvisory(readClientAdvisory(data));
            break;
          case "credential-revoked":
            window.dispatchEvent(
              new CustomEvent("omnimedia:credential-revoked"),
            );
            break;
          // ack / pong / presence / error: nothing to do here.
          default:
            break;
        }
      };

      ws.onclose = () => {
        // A superseded socket must never drive state or reconnects.
        if (wsRef.current !== ws) return;
        wsRef.current = null;
        if (heartbeatTimer) {
          clearInterval(heartbeatTimer);
          heartbeatTimer = undefined;
        }
        setConnected(false);
        if (!disposed) scheduleReconnect();
      };

      ws.onerror = () => {
        // The close handler drives reconnect; avoid double-scheduling here.
        ws.close();
      };
    };

    const handleOnline = () => {
      setOnline(true);
      // Network is back: drop any pending backoff and retry now. connect()
      // tears down whatever socket (CONNECTING/CLOSING/half-open) remains.
      backoff = 0;
      if (wsRef.current?.readyState !== WebSocket.OPEN) {
        clearTimers();
        connect();
      }
    };
    const handleOffline = () => {
      setOnline(false);
      setConnected(false);
    };
    // A kiosk tab coming back to the foreground after a suspend may hold a
    // half-open socket. If the connection can't prove recent life, replace it
    // now instead of waiting out the watchdog.
    const handleVisibility = () => {
      if (document.visibilityState !== "visible") return;
      const ws = wsRef.current;
      if (
        !ws ||
        ws.readyState !== WebSocket.OPEN ||
        shouldForceReconnect(lastInboundAt, Date.now(), HEARTBEAT_INTERVAL_MS)
      ) {
        backoff = 0;
        clearTimers();
        connect();
      }
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    document.addEventListener("visibilitychange", handleVisibility);

    connect();

    return () => {
      disposed = true;
      clearTimers();
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      document.removeEventListener("visibilitychange", handleVisibility);
      const ws = wsRef.current;
      if (ws) {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(
            JSON.stringify({
              type: "now-playing",
              state: "stopped",
              positionSec: 0,
              volume: 100,
            }),
          );
        }
        ws.onclose = null;
        ws.onerror = null;
        ws.close();
        wsRef.current = null;
      }
      setConnected(false);
      setDeviceId(null);
      setDeviceName(null);
      // The verdict belongs to the credential we just dropped.
      setAdvisory(null);
    };
  }, [token, send]);

  return {
    connected,
    online,
    deviceId,
    deviceName,
    advisory,
    sendNowPlaying,
    sendStatus,
    sendSignal,
    sendDeviceMetric,
    sendCacheReport,
  };
}
